// AI Generated Component
import { Component, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { StatisticsDateComparisonService } from '@monsido/modules/statistics/services/date-comparison/statistics-date-comparison.service';
import moment from 'moment';
import colors from '@monsido/colors/dist/es6/colors';
import { hexToRgba } from '../../helpers/hex-to-rgba';
import { MAX_PAGE_SIZE_FOR_COMPARISON } from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';
import { StatisticsExportBuilderService } from '@monsido/modules/statistics/services/export/statistics-export-builder.service';

@Component({
    selector: 'statistics-most-downloads',
    standalone: false,
    templateUrl: './most-downloads.component.html',
})
export class StatisticsMostDownloadsComponent extends BaseContainerComponentController implements OnInit {
    loading: boolean = false;
    totalUniqueDownloads: number = 0;
    summary: any = {};
    comparisonEnabled: boolean | null = null;
    compareDate: any = { startDate: null, endDate: null };
    noDownloadsResultMessage: string = '';

    domain: any;
    date: any;
    dateFormat: string = '';
    override search: string = '';
    exports: any[] = [];
    downloads: any = [];
    promise: any;
    chartSeries: any[] = [];
    chartLabels: any[] = [];
    chartData: any[] = [];
    chartOverride: any[] = [];
    chartOptions: any = {};

    constructor (
        private statsRepoService: StatsRepoService,
        private stateService: StateService,
        private paramService: ParamService,
        private translateService: TranslateService,
        private dateRangeService: DateRangeService,
        private statisticsDateComparisonService: StatisticsDateComparisonService,
        private monModalService: MonModalService,
        private statisticsExportBuilderService: StatisticsExportBuilderService,
    ) {
        super({ table: true, labels: true, pages: true });
    }

    ngOnInit () {
        this.init();
    }

    init () {
        this.downloads = [];
        this.domain = this.sessionService.domain;
        this.totalUniqueDownloads = 0;
        this.summary = {};
        this.chartSeries = [this.translateService.getString('Total unique downloads')];
        this.chartLabels = [];
        this.chartData = [];
        this.chartOverride = [];
        this.compareDate = { startDate: null, endDate: null };
        this.search = '';

        const noResultsMsg = this.translateService.getString('No downloads found');
        this.noDownloadsResultMessage = noResultsMsg;

        this.chartOptions = {
            legend: {
                display: false,
                position: 'bottom',
                fullWidth: true,
                labels: {
                    boxWidth: 20,
                    padding: 20,
                },
            },
            scales: {
                xAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            min: 0,
                            stepSize: 1,
                        },
                    },
                ],
                yAxes: [
                    {
                        gridLines: {
                            display: false,
                        },
                        ticks: {
                            callback: (value: any) => {
                                const str = String(value);
                                return str.length > 40 ? str.slice(0, 37) + '...' : str;
                            },
                        },
                    },
                ],
            },
            tooltips: {
                intersect: false,
            },
        };

        this.date = this.dateRangeService.setRangeDates();
        this.dateFormat = 'ddd, D. MMM YYYY';
        this.exports = [
            {
                name: this.translateService.getString('Most Downloads'),
                data: {
                    category: 'most_downloads',
                },
            },
        ];
        this.updateDateRange();
    }

    validateDate = (date: any) => this.statisticsDateComparisonService.validateDate(date) || '';

    override onSearch (search: string) {
        this.search = search;
        this.page = 1;
        if (this.comparisonEnabled && this.date && this.compareDate) {
            this.promise = this.onComparisonRangeSelected(
                this.date.startDate,
                this.date.endDate,
                this.compareDate.startDate,
                this.compareDate.endDate,
            );
        } else {
            this.getPage();
        }
    }

    override getPage () {
        const params = {
            limit: this.pageSize,
            page: this.page || 1,
            from: this.date.startDate?.format('YYYY-MM-DD'),
            to: this.date.endDate?.format('YYYY-MM-DD'),
            search: this.search,
        };

        this.loading = true;

        this.promise = Promise.all([this.statsRepoService.getMostDownloads(params, contextTokens.NO_PARSE_SEARCH)
            .then((data) => {
                this.downloads = data;
                this.downloads.currentpage = this.page;
                this.totalUniqueDownloads = data.reduce((total, downloads: any) => {
                    return total + parseInt(downloads.downloads);
                }, 0);
                this.downloads.sort((a, b) => {
                    return b.downloads - a.downloads;
                });
                this.chartLabels = this.downloads.map((downloads) => {
                    return downloads.title ? downloads.title : downloads.label;
                });
                this.chartData = [
                    this.downloads.map((downloads) => {
                        return downloads.downloads;
                    }),
                ];
                this.chartOverride = [
                    {
                        borderWidth: 1,
                        borderColor: [],
                        backgroundColor: [],
                    },
                ];
                for (let i = 0; i < this.downloads.length; i++) {
                    this.chartOverride[0].borderColor.push(colors['main-1']);
                    this.chartOverride[0].backgroundColor.push(colors['main-1']);
                }

                this.setNoSearchResultsMsgs(this.downloads, this.search);
            }, () => {}), this.getSummary()])
            .finally(() => this.loading = false);

    }

    getSummary () {
        const params = {
            domainId: this.stateService.params.domainId,
            from: this.date.startDate?.format('YYYY-MM-DD'),
            to: this.date.endDate?.format('YYYY-MM-DD'),
            type: 'total',
        };

        return this.statsRepoService.getSummary(params).then((data: any) => {
            this.summary = data;
        })
            .finally(() => {
                this.loading = false;
            });
    }

    async onComparisonRangeSelected (currentStartDate: any, currentEndDate: any, compareStartDate: any, compareEndDate: any) {
        this.loading = true;
        const currentDataRequestParams = {
            limit: MAX_PAGE_SIZE_FOR_COMPARISON,
            page: this.page || 1,
            domainId: this.stateService.params.domainId,
            search: this.search,
            from: currentStartDate?.format('YYYY-MM-DD'),
            to: currentEndDate?.format('YYYY-MM-DD'),
        };
        const compareDataRequestParams = {
            limit: MAX_PAGE_SIZE_FOR_COMPARISON,
            page: this.page || 1,
            domainId: this.stateService.params.domainId,
            search: this.search,
            from: compareStartDate?.format('YYYY-MM-DD'),
            to: compareEndDate?.format('YYYY-MM-DD'),
        };

        const [currentPages, comparePages] = await Promise.all([
            this.statsRepoService.getMostDownloads(currentDataRequestParams, contextTokens.NO_PARSE_SEARCH),
            this.statsRepoService.getMostDownloads(compareDataRequestParams, contextTokens.NO_PARSE_SEARCH),

            this.getSummary(),
        ]);

        const currentPagesMap = new Map();

        for (let i = 0; i < currentPages.length; i++) {
            const page = currentPages[i];
            const pageCompare = page.compare ? page.compare : null;
            const computedPage = {
                ...page,
                compare: {
                    ...(pageCompare ? {
                        downloads: pageCompare.downloads,
                        pageviews: pageCompare.pageviews,
                    } : {
                        downloads: 0,
                        pageviews: 0,
                    }),
                },
            };
            currentPagesMap.set(page.label, computedPage);
        }

        for (let i = 0; i < comparePages.length; i++) {
            const comparePage = comparePages[i];
            const existedPageInMap = currentPagesMap.get(comparePage.label);
            if (existedPageInMap) {
                existedPageInMap.compare = comparePage;
            }
        }

        const computedPages = Array.from(currentPagesMap.values());

        this.downloads = computedPages;
        this.chartSeries = [
            this.translateService.getString('Total unique downloads'),
            this.translateService.getString('Total unique downloads (comp.)'),
        ];
        this.chartData = [[], []];
        this.chartOverride = [
            {
                borderWidth: 1,
                borderColor: [],
                backgroundColor: [],
            },
            {
                borderWidth: 1,
                borderColor: [],
                backgroundColor: [],
            },
        ];
        this.chartLabels = [];

        for (let i = 0; i < computedPages.length; i++) {
            const page = computedPages[i];
            this.chartData[0].push(page.downloads || 0);
            this.chartData[1].push(page.compare.downloads || 0);

            this.chartLabels.push(page.title || page.label);

            this.chartOverride[0].borderColor.push(colors['main-1']);
            this.chartOverride[0].backgroundColor.push(colors['main-1']);
            this.chartOverride[1].borderColor.push(hexToRgba(colors['main-1'], 0.25));
            this.chartOverride[1].backgroundColor.push(hexToRgba(colors['main-1'], 0.25));
        }

        this.setNoSearchResultsMsgs(this.downloads, this.search);
        this.loading = false;
    }

    setNoSearchResultsMsgs (results: any[], searchTerm: string) {
        const noResultsMsg = this.translateService.getString('No downloads found');
        const noResultsMsgSuggestion = this.translateService.getString(
            'No downloads were found. Try searching for the document URL instead of the document title.',
        );

        if (!results.length && !searchTerm) {
            this.noDownloadsResultMessage = noResultsMsg;
        }

        if (!results.length && searchTerm) {
            this.noDownloadsResultMessage = noResultsMsgSuggestion;
        }
    }

    onPageChangeLocal (page: number) {
        this.page = page;
        if (this.comparisonEnabled && this.date && this.compareDate) {
            this.promise = this.onComparisonRangeSelected(
                this.date.startDate,
                this.date.endDate,
                this.compareDate.startDate,
                this.compareDate.endDate,
            );
        } else {
            this.getPage();
            this.getSummary();
        }
    }

    override onPageSizeChange (pageSize: number) {
        this.pageSize = pageSize;
        this.page = 1;
        this.setExportCategoryRef();
        this.getPage();
    }

    updateCompareDate (e: any): Promise<string> | void {
        const isFutureDates = this.statisticsDateComparisonService.isFutureDate(e.startDate) ||
            this.statisticsDateComparisonService.isFutureDate(e.endDate);

        this.comparisonEnabled = e.startDate && e.endDate && !isFutureDates;

        if (!this.comparisonEnabled && this.chartData?.length > 1) {
            this.chartData.pop();
        }

        if (isFutureDates) {
            return this.monModalService.alert(
                this.translateService.getString(
                    'The start and end date cannot be in the future, please enter the compare dates again',
                ),
            );
        }

        if (this.comparisonEnabled) {
            this.compareDate.startDate = e.startDate;
            this.compareDate.endDate = e.endDate;
            this.pageSize = 10;
            this.page = 1;
            this.promise = this.onComparisonRangeSelected(
                this.date.startDate,
                this.date.endDate,
                this.compareDate.startDate,
                this.compareDate.endDate,
            );
            return;
        }

        // If comparison is not enabled, ensure a deterministic flow
        this.getPage();
        this.getSummary();
    }

    updateDateRange () {
        if (!this.exports[0].data.date) {
            this.exports[0].data.date = {};
        }
        this.exports[0].data.date.startDate = this.date.startDate;
        this.exports[0].data.date.endDate = this.date.endDate;
        if (this.date.startDate && this.date.endDate) {
            this.paramService.setParams({
                from: moment(this.date.startDate),
                to: moment(this.date.endDate),
            });
        }

        this.setExportCategoryRef();
        this.getPage();
        this.getSummary();
    }

    setExportCategoryRef () {
        this.exports[0].data.category_ref = this.statisticsExportBuilderService.buildCategoryRef(
            this.date.startDate,
            this.date.endDate,
            'YYYY-MM-DD',
            1000,
        );
    }
}
